import { useEffect, useRef, useState } from 'react';
import { PhotoSlider } from 'react-photo-view';
import { X, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectFile } from '../types';
import { useModalStack } from '../context/ModalStackContext';
import { isImageMedia } from '@/lib/media/mediaTypes';
import { downloadMedia } from '@/lib/media/downloadMedia';
import 'react-photo-view/dist/react-photo-view.css';

interface ImageGalleryProps {
  images: ProjectFile[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  enableDownload?: boolean;
}

export default function ImageGallery({
  images,
  initialIndex,
  isOpen,
  onClose,
  enableDownload = true,
}: ImageGalleryProps) {
  const modalId = useRef(`gallery-${Date.now()}`).current;
  const { registerModal } = useModalStack();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // סינון קבצי תמונות בלבד (Filtering image files only)
  // type-only check: matches backend 'IMAGE' and legacy raw-MIME 'image/*' alike, no filename heuristics.
  const imageFiles = images.filter((f) => isImageMedia({ type: f.type }));

  // רישום במחסנית המודאלים (Modal stack registration)
  useEffect(() => {
    if (!isOpen || imageFiles.length === 0) return;

    return registerModal({
      id: modalId,
      priority: 9999,
      onClose,
    });
  }, [isOpen, imageFiles.length, registerModal, modalId, onClose]);

  // סנכרון האינדקס כשהוא משתנה מבחוץ (Syncing index prop)
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  async function handleDownload(file: ProjectFile) {
    const result = await downloadMedia({
      url: file.url,
      fileName: file.name || 'download.png',
      mimeType: file.type || 'application/octet-stream',
    });

    if (!result.success) {
      toast.error('ההורדה נכשלה', { style: { direction: 'rtl' } });
      return;
    }

    // Native saves to the public Downloads folder and confirms it; the web path
    // (silent before this migration) keeps its no-success-toast behavior.
    if (result.platform === 'native') {
      toast.success('הקובץ נשמר בתיקיית ההורדות', { style: { direction: 'rtl' } });
    }
  }

  // אין צורך לרנדר כלום אם החלון סגור או שאין תמונות
  if (!isOpen || imageFiles.length === 0) {
    return null;
  }

  // המרת הקבצים לפורמט ש-PhotoSlider מצפה לקבל
  const sliderImages = imageFiles.map((file) => ({
    src: file.url,
    key: file.id,
    fileData: file, // שומרים את האובייקט המקורי כדי לגשת אליו בסרגל הכלים
  }));

  return (
    <PhotoSlider
      images={sliderImages}
      visible={isOpen}
      onClose={onClose}
      index={currentIndex}
      onIndexChange={setCurrentIndex}
      toolbarRender={({ index }) => {
        const currentFile = sliderImages[index]?.fileData;

        return (
          <div className="fixed inset-0 pointer-events-none z-[1000000000]">
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent pointer-events-auto">

              {/* כפתור סגירה (Close button) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors text-white"
                aria-label="סגור"
              >
                <X className="w-6 h-6" />
              </button>

              {/* מונה תמונות (Image counter) */}
              <div className="text-white text-lg font-medium bg-black/50 px-4 py-2 rounded-full">
                {imageFiles.length} / {index + 1}
              </div>

              {/* כפתור הורדה (Download button) */}
              {enableDownload && currentFile && (
                <button
                  onClick={() => handleDownload(currentFile)}
                  className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors text-white"
                  aria-label="הורד תמונה"
                >
                  <Download className="w-6 h-6" />
                </button>
              )}

            </div>
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-3 p-4 bg-gradient-to-t from-black/70 to-transparent pointer-events-auto">
              <span className="text-white text-sm bg-black/50 px-3 py-1 rounded-full whitespace-pre-line text-center">
                {currentFile?.notes || ``}
              </span>
            </div>

          </div>
        );
      }}
    />
  );
}